
export class CleanupManager {
  private static instance: CleanupManager;
  private cleanupTasks = new Set<() => void>();
  private intervalIds = new Set<NodeJS.Timeout>();
  private timeoutIds = new Set<NodeJS.Timeout>();
  private abortControllers = new Set<AbortController>();

  private constructor() {}

  static getInstance(): CleanupManager {
    if (!CleanupManager.instance) {
      CleanupManager.instance = new CleanupManager();
    }
    return CleanupManager.instance;
  }

  // Register cleanup tasks
  addCleanupTask(task: () => void): void {
    this.cleanupTasks.add(task);
  }

  removeCleanupTask(task: () => void): void {
    this.cleanupTasks.delete(task);
  }

  // Track intervals for cleanup
  createInterval(callback: () => void, delay: number): NodeJS.Timeout {
    const id = setInterval(callback, delay);
    this.intervalIds.add(id);
    return id;
  }

  clearInterval(id: NodeJS.Timeout): void {
    clearInterval(id);
    this.intervalIds.delete(id);
  }

  // Track timeouts for cleanup
  createTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const id = setTimeout(callback, delay);
    this.timeoutIds.add(id);
    return id;
  }

  clearTimeout(id: NodeJS.Timeout): void {
    clearTimeout(id);
    this.timeoutIds.delete(id);
  }

  // Track abort controllers
  createAbortController(): AbortController {
    const controller = new AbortController();
    this.abortControllers.add(controller);
    return controller;
  }

  removeAbortController(controller: AbortController): void {
    this.abortControllers.delete(controller);
  }

  // Execute all cleanup tasks
  cleanup(): void {
    // Run all registered cleanup tasks
    this.cleanupTasks.forEach(task => {
      try {
        task();
      } catch (error) {
        console.warn('Cleanup task failed:', error);
      }
    });

    // Clear all intervals
    this.intervalIds.forEach(id => clearInterval(id));
    this.intervalIds.clear();

    // Clear all timeouts
    this.timeoutIds.forEach(id => clearTimeout(id));
    this.timeoutIds.clear();

    // Abort all controllers
    this.abortControllers.forEach(controller => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    this.abortControllers.clear();

    // Clear cleanup tasks
    this.cleanupTasks.clear();

    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 CleanupManager: All resources cleaned up');
    }
  }

  // Get current resource counts for monitoring
  getResourceCounts() {
    return {
      cleanupTasks: this.cleanupTasks.size,
      intervals: this.intervalIds.size,
      timeouts: this.timeoutIds.size,
      abortControllers: this.abortControllers.size,
    };
  }
}

// Utility hook for managing cleanup in React components
export const useCleanup = () => {
  const cleanupManager = CleanupManager.getInstance();
  
  return {
    addCleanupTask: cleanupManager.addCleanupTask.bind(cleanupManager),
    removeCleanupTask: cleanupManager.removeCleanupTask.bind(cleanupManager),
    createInterval: cleanupManager.createInterval.bind(cleanupManager),
    clearInterval: cleanupManager.clearInterval.bind(cleanupManager),
    createTimeout: cleanupManager.createTimeout.bind(cleanupManager),
    clearTimeout: cleanupManager.clearTimeout.bind(cleanupManager),
    createAbortController: cleanupManager.createAbortController.bind(cleanupManager),
    removeAbortController: cleanupManager.removeAbortController.bind(cleanupManager),
  };
};
